/**
 * SA-717 (email triage spec T18, T19): a person's decision on a triaged
 * email. Snake_case wire bodies, as the backend reads them. The caller
 * refetches the conversation detail instead of reading any answer.
 */
import { apiClient, ApiError } from './apiClient'
import type { CustomerUpdateApplyPayload } from '@/types/commerce'

const base = (conversationId: string) => `/api/inbox/conversations/${conversationId}/triage`

export const triageService = {
  /** Approve the proposal (no `kind`) or decide another kind. `ledger_id`
   * names a reply's row; without it the thread's first message is meant.
   * SA-1030: `record_number` sends a group message to that record as an update.
   * SA-1095 PR 2: `outcome` is a closing kind's pick (null = close nothing); send the key only for a kind that has outcomes. */
  decide: (conversationId: string, body: { kind?: string; ledger_id?: string; record_number?: string; outcome?: string | null }) =>
    apiClient.put(base(conversationId), body),
  /** "Not an enquiry": close the record as rejected. SA-945 S2: on a WhatsApp
   * group thread `ledger_id` names the message whose record it is (required
   * there); on email it is optional and the record is the thread's. */
  reject: (conversationId: string, body: { kind?: string; reason?: string; ledger_id?: string } = {}) =>
    apiClient.post(`${base(conversationId)}/reject`, body),
  /** SA-770: undo "Not an enquiry": the record reopens and the thread returns to its kind. */
  reinstate: (conversationId: string, body: { ledger_id?: string } = {}) =>
    apiClient.post(`${base(conversationId)}/reinstate`, body),
  /** SA-776: run a message's handler again (spec T20), the mailbox's endpoint. */
  rerun: (mailboxId: string, ledgerId: string) =>
    apiClient.post(`/api/mailboxes/${mailboxId}/messages/${ledgerId}/rerun`, {}),
  /** SA-1042: Accept a customer update's proposal from the Inbox. The record's
   * own apply endpoint, the same body the Intake block sends: one place to apply. */
  acceptUpdate: (orderId: string, updateId: string, body: CustomerUpdateApplyPayload) =>
    apiClient.post(`/api/commerce/orders/${orderId}/customer-updates/${updateId}/apply`, body),
}

/** The server's sentence for a refusal: `{code, message}` on the triage
 * refusals. A code is never printed, and neither is a permission key: the
 * route-level 403 is a plain string naming one (SA-771). */
export function triageErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const detail = err.detail
    if (err.status === 403 && typeof detail === 'string') {
      return 'You do not have permission to do that. Ask an administrator.'
    }
    if (typeof detail === 'string' && detail) return detail
    if (detail && typeof detail === 'object') {
      const message = (detail as { message?: unknown }).message
      if (typeof message === 'string' && message) return message
    }
  }
  return 'That did not go through. Reload and try again.'
}
